import { styled as muiStyled } from '@mui/material/styles';
import React from 'react';

import { useDesignTokens } from '~/client/hooks/use-design-tokens';

/**
 * Semantic tones, mapped to the theme's status tokens.
 *
 * Deliberately semantic rather than colour-named: a caller says what a thing
 * *means*, not what shade to paint it, so light and dark mode stay consistent
 * and a palette change lands everywhere at once.
 */
export type StatusTone = 'success' | 'warning' | 'danger' | 'info' | 'neutral';

export interface StatusPillProps {
  className?: string;
  tone: StatusTone;
  label: string;
  size?: 'small' | 'medium';
}

/**
 * A compact status indicator.
 *
 * `base/chip` is an unstyled MUI passthrough and `base/status-badge` only
 * understands risk and alignment levels, so neither covers "show this state
 * with the right semantic colour". This does, from tokens alone.
 */
const StatusPillImpl: React.FC<StatusPillProps> = ({
  className,
  tone,
  label,
  size = 'small',
}) => {
  const tokens = useDesignTokens();
  const fg = tokens.status[tone];
  const bg = tokens.status[`${tone}Bg` as keyof typeof tokens.status];

  return (
    <span
      className={`${className ?? ''} size-${size}`}
      style={{ color: fg, backgroundColor: bg as string }}
    >
      {label}
    </span>
  );
};

const StatusPill = muiStyled(StatusPillImpl)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border-radius: 999px;
  font-weight: 600;
  white-space: nowrap;
  line-height: 1;
  /* Statuses read as labels, not prose: upright and upper-case, with the
     letter-spacing upper-case needs to stay legible at this size. */
  text-transform: uppercase;
  font-style: normal;
  letter-spacing: 0.04em;

  &.size-small {
    padding: 4px 10px;
    font-size: 12px;
  }

  &.size-medium {
    padding: 6px 12px;
    font-size: 13px;
  }
`;

export default StatusPill;
